import React from "react";
import { useToast } from "@chakra-ui/react";
import { StepperModal } from "../../../_components/Modals";
import { CompanyInformationStep } from "./steps/1-company-information";
import { ContactDetailsStep } from "./steps/2-contact-details";
import { CommunityInformationStep } from "./steps/3-community-information";
import { DemoPreferenceStep } from "./steps/4-demo-preference";
import {
  CommunityDemoRequestProvider,
  useCommunityDemoRequest,
} from "../context/community-demo-request.context";
import {
  CompanyInformationFormData,
  ContactDetailsFormData,
  CommunityInformationFormData,
  DemoPreferenceFormData,
} from "../schemas/schema";
import { logEvent } from "../../../helpers/analytics";

interface CommunityDemoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CommunityDemoRequestModalContent: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const {
    currentStep,
    nextStep,
    previousStep,
    submitForm,
    resetForm,
    updateCompanyInfo,
    updateContactDetails,
    updateCommunityInfo,
    updateDemoPreference,
    getAllFormData,
    getCompanyInfo,
    getContactDetails,
    getCommunityInfo,
    getDemoPreference,
    isLoading,
  } = useCommunityDemoRequest();

  const toast = useToast();

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Tell us about your organization";
      case 2:
        return "Contact details";
      case 3:
        return "Community information";
      case 4:
        return "Demo preferences";
      default:
        return "Community Demo Request";
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCompanyInfoComplete = (data: CompanyInformationFormData) => {
    updateCompanyInfo(data);
    nextStep();
  };

  const handleContactDetailsComplete = (data: ContactDetailsFormData) => {
    updateContactDetails(data);
    nextStep();
  };

  const handleCommunityInfoComplete = (data: CommunityInformationFormData) => {
    updateCommunityInfo(data);
    nextStep();
  };

  const handleDemoPreferenceComplete = async (data: DemoPreferenceFormData) => {
    updateDemoPreference(data);

    // Get all form data and prepare for submission
    const allFormData = { ...getAllFormData(), ...data };
    
    // Submit using the mutation
    await submitForm({
      variables: {
        communityManagementDemoRequestDto: {
          organization: allFormData.organization!,
          roleInOrganization: allFormData.roleInOrganization!,
          firstName: allFormData.firstName!,
          lastName: allFormData.lastName!,
          email: allFormData.email!,
          phone: allFormData.phone!,
          typeOfCommunity: allFormData.typeOfCommunity!,
          communitySize: allFormData.communitySize!,
          communityTalent: allFormData.communityTalent!,
          majorChallenges: allFormData.majorChallenges!,
          digitalToolsUsed: allFormData.digitalToolsUsed,
          preferredDemoDate: allFormData.preferredDate.toISOString(),
          preferredDemoTime: allFormData.preferredTime,
        },
      },
      onCompleted: () => {
        toast({
          title: "Request Submitted Successfully",
          description: "We'll contact you soon with a demo and next steps.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        handleClose();
        logEvent("community_demo_request_submit_success");
      },
      onError: (error) => {
        toast({
          title: "Submission Failed",
          description:
            "Please try again or contact support if the issue persists.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        logEvent("community_demo_request_error", { error: error.message });
      },
    });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CompanyInformationStep
            onStepComplete={handleCompanyInfoComplete}
            isLoading={isLoading}
            defaultValues={getCompanyInfo()}
          />
        );
      case 2:
        return (
          <ContactDetailsStep
            onStepComplete={handleContactDetailsComplete}
            onPrevious={previousStep}
            isLoading={isLoading}
            defaultValues={getContactDetails()}
          />
        );
      case 3:
        return (
          <CommunityInformationStep
            onStepComplete={handleCommunityInfoComplete}
            onPrevious={previousStep}
            isLoading={isLoading}
            defaultValues={getCommunityInfo()}
          />
        );
      case 4:
        return (
          <DemoPreferenceStep
            onStepComplete={handleDemoPreferenceComplete}
            onPrevious={previousStep}
            isLoading={isLoading}
            defaultValues={getDemoPreference()}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StepperModal
      isOpen={true}
      onClose={handleClose}
      title={getStepTitle()}
      onSubmit={() => {}}
      currentStep={currentStep}
      totalSteps={4}
      isLoading={isLoading}
      onReset={resetForm}
    >
      {renderCurrentStep()}
    </StepperModal>
  );
};

export const CommunityDemoRequestModal: React.FC<CommunityDemoRequestModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <CommunityDemoRequestProvider>
      <CommunityDemoRequestModalContent onClose={onClose} />
    </CommunityDemoRequestProvider>
  );
};


